import type { LayoutPage } from './api';

/**
 * Remove the page whose `page` number === `pageNumber` and renumber every
 * following page DOWN by one, so `page` stays a contiguous 1-based sequence
 * with no gaps. This is the single source of truth for "delete page N → N+1
 * becomes N, N+2 becomes N+1, …" used by the scroller, the desktop tools
 * panel, and the mobile hold-to-delete gesture.
 *
 * Pure: returns a NEW array and never mutates the input or its page objects.
 * If no page matches `pageNumber`, the original array reference is returned
 * unchanged (callers can cheaply detect a no-op via `===`).
 *
 * Renumbering is purely positional — there is no stored page key in the DB
 * (pages are a JSONB array; `page` is a derived 1-indexed counter), and PDF
 * export iterates by array position, so this keeps editor, preview, PDF and
 * the ÷4 print padding all in agreement after a delete.
 */
export function renumberAfterDelete(
  pages: LayoutPage[],
  pageNumber: number,
): LayoutPage[] {
  const idx = pages.findIndex((p) => p.page === pageNumber);
  if (idx === -1) return pages;
  const after = pages.slice(idx + 1).map((p) => ({ ...p, page: p.page - 1 }));
  return [...pages.slice(0, idx), ...after];
}
